'use client'

import React, { useEffect, useState, ReactNode, useRef } from 'react'
import ReactDOM from 'react-dom'
import { AnimatePresence, motion, useDragControls } from 'framer-motion'
import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import classNames from 'classnames'

export type AlertData = {
  title: string
  description?: ReactNode | null
  cancelable?: boolean
  onConfirm?: () => void
}

export type CoinData = {
  text: string
}

type DialogStore = {
  active: string[]
  alertData?: AlertData
  coinData?: CoinData
  push: (token: string) => void
  pop: () => void
  remove: (token: string) => void
  has: (token: string) => boolean
  alert: (data: AlertData) => void
  coin: (data: CoinData) => void
}

export const useDialogStore = create(
  immer<DialogStore>((set, get) => ({
    active: [],
    push: (token: string) =>
      set((state) => {
        state.active.push(token)
      }),
    pop: () =>
      set((state) => {
        state.active.pop()
      }),
    remove: (token) =>
      set((state) => {
        state.active = state.active.filter((t) => token !== t)
      }),
    has: (token: string) => {
      return get().active.includes(token)
    },
    alert: (data: AlertData) => {
      set((state) => {
        state.active.push('global-alert')
        state.alertData = data
      })
    },
    coin: (data: CoinData) => {
      set((state) => {
        state.active.push('heart-coin')
        state.coinData = data
      })
    },
  })),
)

export type FullScreenBackdropProps = {
  children: React.ReactNode
  token: string
  onClose?: () => void
  permenant?: boolean
  transition?: 'fade' | 'sheet'
  className?: string
}

export const FullScreenBackdrop = ({
  children,
  token,
  onClose,
  permenant,
  transition,
  className,
}: FullScreenBackdropProps) => {
  const [loaded, setLoaded] = useState(false)

  const modal = useDialogStore()

  useEffect(() => {
    setLoaded(true)
  }, [])

  const close = () => {
    modal.pop()
    onClose?.()
  }

  const hidden = !loaded || !modal.active.find((t) => t === token)

  const variants = {
    visible: { opacity: 1 },
    hidden: { opacity: 0 },
  }

  const render = () => {
    switch (transition) {
      case 'sheet':
        return <Slide close={close}>{children}</Slide>
      case 'fade':
      default:
        return <Fade>{children}</Fade>
    }
  }

  const component = (
    <AnimatePresence>
      {!hidden && (
        <motion.div
          variants={variants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          className={classNames(
            'fixed left-0 top-0 w-full h-full flex justify-center z-50',
            { 'items-center': !transition || transition === 'fade' },
            { 'items-end': transition === 'sheet' },
            className,
          )}
        >
          <div
            onClick={!permenant ? close : undefined}
            className="absolute left-0 top-0 w-full h-full bg-black bg-opacity-40 -z-10"
          />
          {render()}
        </motion.div>
      )}
    </AnimatePresence>
  )

  if (!loaded) {
    return null
  }

  return ReactDOM.createPortal(
    component,
    document.getElementById('root-backdrop')!,
  )
}

type BackDropBodyProps = {
  children: React.ReactNode
  close?: () => void
}

const Fade = ({ children }: BackDropBodyProps) => {
  return (
    <div className="w-full max-w-[95%] flex items-center justify-center pointer-events-none">
      <div className="pointer-events-auto max-w-full">{children}</div>
    </div>
  )
}

const Slide = ({ children, close }: BackDropBodyProps) => {
  const slideVariants = {
    visible: { y: 0 },
    hidden: { y: '100%' },
  }

  const control = useDragControls()
  const ref = useRef<HTMLDivElement>(null)

  return (
    <motion.div
      ref={ref}
      drag="y"
      dragConstraints={{
        top: 0,
      }}
      dragSnapToOrigin
      onDragEnd={(e, info) => {
        if (info.offset.y > 120) {
          close?.()
        }
      }}
      dragControls={control}
      variants={slideVariants}
      transition={{
        bounce: false,
      }}
      className="w-full flex justify-center max-w-[375px] bg-dialog rounded-t-3xl pb-4 px-4 flex-col"
    >
      {children}
    </motion.div>
  )
}
